import { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Chip,
  Box,
  Button,
  Tooltip,
  ButtonGroup,
  Stack,
} from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import {
  Edit,
  Delete,
  Visibility,
  CheckCircle,
  Schedule,
  Cancel,
  Instagram,
  PlaylistPlay,
  ExpandMore,
  ExpandLess
} from '@mui/icons-material';
import ShowcaseMediaDisplay from './ShowcaseMediaDisplay';
import { useTranslation } from 'react-i18next';

export interface ShowcaseMedia {
  key: string;
  mediaType: string;
}

export interface ShowcaseData {
  _id: string;
  media?: ShowcaseMedia[];
  content?: string;
  status?: string;
  planIds?: string[];
  isFromInstagram?: boolean;
}

interface ShowcaseCardProps {
  showcase: ShowcaseData;
  onEdit?: (showcase: ShowcaseData) => void;
  onDelete?: (showcase: ShowcaseData) => void;
  onView?: (showcase: ShowcaseData) => void;
  showActions?: boolean;
  showStatus?: boolean;
  showContent?: boolean;
  contentMaxLength?: number;
  elevation?: number;
  sx?: SxProps<Theme>;
}

const ShowcaseCard: React.FC<ShowcaseCardProps> = ({
  showcase,
  onEdit,
  onDelete,
  onView,
  showActions = true,
  showStatus = true,
  showContent = true,
  contentMaxLength = 60,
  elevation = 1,
  sx = {}
}) => {
  const { t } = useTranslation();
  const [expandedContent, setExpandedContent] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'pending':
        return 'warning';
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string): React.ReactElement | null => {
    switch (status) {
      case 'approved':
        return <CheckCircle sx={{ fontSize: '1rem' }} />;
      case 'pending':
        return <Schedule sx={{ fontSize: '1rem' }} />;
      case 'rejected':
        return <Cancel sx={{ fontSize: '1rem' }} />;
      default:
        return null;
    }
  };

  const truncatedContent = showcase.content?.length && showcase.content.length > contentMaxLength
    ? showcase.content.substring(0, contentMaxLength) + '...'
    : showcase.content || '';

  const shouldShowExpand = showcase.content?.length && showcase.content.length > contentMaxLength;

  const handleEdit = () => {
    console.log('Edit clicked for showcase:', showcase._id);
    onEdit?.(showcase);
  };

  const handleDelete = () => {
    console.log('Delete clicked for showcase:', showcase._id);
    onDelete?.(showcase);
  };

  const handleView = () => {
    console.log('View clicked for showcase:', showcase._id);
    onView?.(showcase);
  };

  return (
    <Card
      elevation={isHovered ? elevation + 2 : elevation}
      sx={{
        position: 'relative',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        cursor: 'pointer',
        borderRadius: 3,
        overflow: 'hidden',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: (theme: Theme) => theme.shadows[8]
        },
        ...sx
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Media Display */}
      <Box sx={{ position: 'relative' }}>
        <ShowcaseMediaDisplay
          media={showcase.media || []}
          height={200}
          showMediaCount={false}
          clickable={true}
          borderRadius={0}
        />


        {/* Status Badge */}
        {showStatus && (
          <Box sx={{ position: 'absolute', top: 12, right: 12, zIndex: 10 }}>
            <Chip
              size="small"
              label={t(`showcaseCard.status.${showcase.status}` as string, showcase.status as string)}
              color={getStatusColor(showcase.status || '')}
              icon={getStatusIcon(showcase.status || '') || undefined}
              variant="filled"
              sx={{
                bgcolor: (theme: Theme) => {
                  const color = getStatusColor(showcase.status || '');
                  if (color === 'default') return theme.palette.grey[500];
                  return theme.palette[color].main;
                },
                color: 'white',
                fontWeight: 'bold',
                boxShadow: 2
              }}
            />
          </Box>
        )}
      </Box>

      {/* Card Content */}
      <CardContent sx={{ p: 3 }}>
        {/* Main Content */}
        {showContent && showcase.content && (
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="body1"
              sx={{
                lineHeight: 1.6,
                color: 'text.primary',
                fontWeight: 400
              }}
            >
              {expandedContent ? showcase.content : truncatedContent}
            </Typography>

            {shouldShowExpand && (
              <Button
                size="small"
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.stopPropagation();
                  setExpandedContent(!expandedContent);
                }}
                startIcon={expandedContent ? <ExpandLess /> : <ExpandMore />}
                sx={{
                  mt: 1,
                  textTransform: 'none',
                  fontWeight: 500,
                  minWidth: 'auto',
                  p: 0.5
                }}
              >
                {expandedContent
                  ? t('showcaseCard.showLess', 'Show less')
                  : t('showcaseCard.showMore', 'Show more')
                }
              </Button>
            )}
          </Box>
        )}

        {/* Metadata Row */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
          {/* Left side - Additional metadata */}
          <Stack direction="row" spacing={1} alignItems="center">
            {showcase.isFromInstagram && (
              <Tooltip title={t('showcaseCard.fromInstagram', 'Imported from Instagram')}>
                <Chip
                  size="small"
                  icon={<Instagram sx={{ fontSize: '0.9rem !important' }} />}
                  label="Instagram"
                  sx={{
                    bgcolor: '#E1306C',
                    color: 'white',
                    fontSize: '0.75rem',
                    height: 24,
                    '& .MuiChip-icon': {
                      color: 'white'
                    }
                  }}
                />
              </Tooltip>
            )}

            {showcase.planIds && showcase.planIds.length > 0 && (
              <Tooltip title={t('showcaseCard.linkedToPlans', 'Linked to {{count}} service plan(s)', { count: showcase.planIds.length })}>
                <Chip
                  size="small"
                  icon={<PlaylistPlay sx={{ fontSize: '0.9rem !important' }} />}
                  label={`${showcase.planIds.length} ${showcase.planIds.length === 1 ? 'plan' : 'plans'}`}
                  variant="outlined"
                  sx={{
                    fontSize: '0.75rem',
                    height: 24,
                    borderColor: 'primary.main',
                    color: 'primary.main'
                  }}
                />
              </Tooltip>
            )}
          </Stack>

          {/* Right side - Empty space where media count was */}
        </Box>


        {/* Action Buttons */}
        {showActions && (onEdit || onDelete || onView) && (
          <Box sx={{
            mt: 2,
            display: 'flex',
            gap: 1,
            justifyContent: 'flex-end'
          }}>
            <ButtonGroup size="small" variant="outlined">
              {onView && (
                <Button
                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation();
                    handleView();
                  }}
                  startIcon={<Visibility />}
                  sx={{ textTransform: 'none' }}
                >
                  {t('showcaseCard.view', 'View')}
                </Button>
              )}
              {onEdit && (
                <Button
                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation();
                    handleEdit();
                  }}
                  startIcon={<Edit />}
                  sx={{ textTransform: 'none' }}
                >
                  {t('showcaseCard.edit', 'Edit')}
                </Button>
              )}
              {onDelete && (
                <Button
                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                    console.log('Mobile delete button clicked');
                    e.stopPropagation();
                    handleDelete();
                  }}
                  startIcon={<Delete />}
                  color="error"
                  sx={{ textTransform: 'none' }}
                >
                  {t('showcaseCard.delete', 'Delete')}
                </Button>
              )}
            </ButtonGroup>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default ShowcaseCard;